import { useEffect, useMemo, useState } from "react";
import { IRCA5WhysAnalisys } from "./RCA5WhysAnalisysInterfaces";
import { Button, Divider, IconButton, TextField } from "@mui/material";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import MainCardButtonAction from "components/MainCardButtonAction";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useStyles } from "./RCA5WhysAnalisys.styles";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import { I5WhyAnalisys, IWhy } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import NoData from "components/NoData/NoData.view";
import useRolesPermissions from "hooks/useRolesPermissions";
import { useTranslation } from "react-i18next";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import RCAConfirmDeleteModal from "pages/Corporate/RCA/components/RCAConfirmDeleteModal/RCAConfirmDeleteModal";

const RCA5WhysAnalisys = ({ rca, onSave }: IRCA5WhysAnalisys) => {
    const [fiveWhyAnalisys, setFiveWhyAnalisys] = useState<Partial<I5WhyAnalisys>[]>(rca.five_why_analyses);
    const [isEditingProblemStatement, setIsEditingProblemStatement] = useState<boolean>(false);
    const [isEditingRootCauseConclusion, setIsEditingRootCauseConclusion] = useState<boolean>(false);
    const [questionToEdit, setQuestionToEdit] = useState<number | null>(null);
    const [newQuestionText, setNewQuestionText] = useState<string>("");
    const [newAnswerText, setNewAnswerText] = useState<string>("");
    const [addNewWhy, setAddNewWhy] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [openDeleteProblemStatementModal, setOpenDeleteProblemStatementModal] = useState<boolean>(false);
    const [openDeleteConclusionModal, setOpenDeleteConclusionModal] = useState<boolean>(false);
    const [openDeleteWhyModal, setOpenDeleteWhyModal] = useState<IWhy | null>(null);

    const rcaOwnershipIds = useMemo(
        () =>
            rca
                ? [
                      ...(rca?.owner?.user_id ? [rca.owner.user_id] : []),
                      ...(rca?.created_by ? [rca?.created_by] : []),
                      ...(rca?.rca_team_members ? rca?.rca_team_members?.map((rtm) => rtm.user.user_id) : []),
                  ]
                : [],
        [rca],
    );
    const { RCA_PERMISSIONS } = useRolesPermissions(rcaOwnershipIds);

    const [rootCauseConclusionText, setRootCauseConclusionText] = useState<string>();
    const [problemStatementText, setProblemStatementText] = useState<string>();

    const mainClasses = useRCAMainStyles();
    const classes = useStyles();
    const { t } = useTranslation();

    useEffect(() => {
        if (rca.five_why_analyses && rca.five_why_analyses.length > 0) {
            setFiveWhyAnalisys(rca.five_why_analyses);
            setRootCauseConclusionText(rca.five_why_analyses[0].root_cause_conclusion || "");
            setProblemStatementText(rca.five_why_analyses[0].problem_statement || "");
        } else {
            setFiveWhyAnalisys([
                {
                    problem_statement: "",
                    root_cause_conclusion: "",
                    why: [],
                    rca_id: rca.rca_id,
                } as Partial<I5WhyAnalisys>,
            ]);
        }
    }, [rca]);

    const handleOnSave = async (data: Partial<I5WhyAnalisys>) => {
        const fiveWhyAnalisysToSave = {
            ...(data.five_why_analysis_id && { five_why_analysis_id: data.five_why_analysis_id }),
            rca_id: rca.rca_id,
            problem_statement: data.problem_statement || "",
            root_cause_conclusion: data.root_cause_conclusion || "",
            why:
                data.why?.map(
                    (item) =>
                        ({
                            ...(item.why_id && { why_id: item.why_id }),
                            ...(item.primary_why_id && { primary_why_id: item.primary_why_id }),
                            ...(item.deleted_at && { deleted_at: item.deleted_at }),
                            text: item.text,
                            rca_id: rca.rca_id,
                            isAnswer: item.isAnswer,
                        } as IWhy),
                ) || [],
        };
        setIsSaving(true);
        await onSave(fiveWhyAnalisysToSave);
        setIsEditingProblemStatement(false);
        setIsEditingRootCauseConclusion(false);
        setQuestionToEdit(null);
        setNewQuestionText("");
        setNewAnswerText("");
        setIsSaving(false);
        setAddNewWhy(false);
    };

    const handleEditionOnProblemStatement = () => {
        setProblemStatementText(rca.five_why_analyses[0]?.problem_statement || "");
        setIsEditingRootCauseConclusion(false);
        setAddNewWhy(false);
        setQuestionToEdit(null);
        setIsEditingProblemStatement(true);
    };

    const handleEditionOnRootCauseConclusion = () => {
        setQuestionToEdit(null);
        setAddNewWhy(false);
        setIsEditingProblemStatement(false);
        setIsEditingRootCauseConclusion(!isEditingRootCauseConclusion);
    };

    const handleAddNewWhy = () => {
        setNewAnswerText("");
        setNewQuestionText("");
        setQuestionToEdit(null);
        setIsEditingRootCauseConclusion(false);
        setIsEditingProblemStatement(false);
        setAddNewWhy(true);
    };

    const handleEditWhy = (why: IWhy) => {
        setIsEditingRootCauseConclusion(false);
        setIsEditingProblemStatement(false);
        setAddNewWhy(false);
        setQuestionToEdit(why.why_id);
        const questionToEdit = fiveWhyAnalisys[0]?.why?.find((item) => item.why_id === why.why_id);
        const answerToEdit = fiveWhyAnalisys[0]?.why?.find((item) => item.primary_why_id === why.why_id);
        setNewQuestionText(questionToEdit?.text || "");
        setNewAnswerText(answerToEdit?.text || "");
    };

    const handleSaveNewWhy = () => {
        const newWhyQuestion = {
            rca_id: rca.rca_id,
            text: newQuestionText,
            primary_why_id: null,
        } as Partial<IWhy>;

        const newAnswer = {
            rca_id: rca.rca_id,
            text: newAnswerText,
            isAnswer: true,
        } as Partial<IWhy>;

        const fiveWhyAnalisysToSave = {
            ...(fiveWhyAnalisys[0].five_why_analysis_id && {
                five_why_analysis_id: fiveWhyAnalisys[0].five_why_analysis_id,
            }),
            rca_id: rca.rca_id,
            problem_statement: fiveWhyAnalisys[0].problem_statement || "",
            root_cause_conclusion: fiveWhyAnalisys[0].root_cause_conclusion || "",
            why: [newWhyQuestion, newAnswer] as IWhy[],
        };
        handleOnSave(fiveWhyAnalisysToSave);
    };

    const handleSaveEditWhy = (why: IWhy) => {
        const currentFiveWhyAnalisys = { ...fiveWhyAnalisys[0] };
        const currentFiveWhyAnalisysWhies = currentFiveWhyAnalisys.why
            ? currentFiveWhyAnalisys.why.map((why) => ({
                  ...why,
              }))
            : [];

        const questionToEditIndex = currentFiveWhyAnalisysWhies.findIndex((item) => item.why_id === why.primary_why_id);
        const answerToEditIndex = currentFiveWhyAnalisysWhies.findIndex((item) => item.why_id === why.why_id);
        if (questionToEditIndex !== -1 && answerToEditIndex !== -1) {
            currentFiveWhyAnalisysWhies[questionToEditIndex].text = newQuestionText;
            currentFiveWhyAnalisysWhies[answerToEditIndex].text = newAnswerText;
            currentFiveWhyAnalisys.why = currentFiveWhyAnalisysWhies;
            handleOnSave(currentFiveWhyAnalisys);
        }
    };

    const handleDeleteWhy = (why: IWhy) => {
        const currentFiveWhyAnalisys = { ...fiveWhyAnalisys[0] };
        const currentFiveWhyAnalisysWhies = currentFiveWhyAnalisys.why
            ? currentFiveWhyAnalisys.why.map((why) => ({
                  ...why,
              }))
            : [];

        const questionToEditIndex = currentFiveWhyAnalisysWhies.findIndex((item) => item.why_id === why.why_id);
        const answerToEditIndex = currentFiveWhyAnalisysWhies.findIndex((item) => item.primary_why_id === why.why_id);
        if (questionToEditIndex !== -1 && answerToEditIndex !== -1) {
            currentFiveWhyAnalisysWhies[questionToEditIndex].deleted_at = new Date();
            currentFiveWhyAnalisysWhies[answerToEditIndex].deleted_at = new Date();
            currentFiveWhyAnalisys.why = [...currentFiveWhyAnalisysWhies];
            handleOnSave(currentFiveWhyAnalisys);
        }
    };

    const handleDeletion = async () => {
        if (openDeleteProblemStatementModal || openDeleteConclusionModal) {
            const currentFiveWhyAnalisys = {
                ...fiveWhyAnalisys[0],
                ...(openDeleteProblemStatementModal ? { problem_statement: "" } : { root_cause_conclusion: "" }),
            };
            await handleOnSave(currentFiveWhyAnalisys);
        } else if (openDeleteWhyModal) {
            handleDeleteWhy(openDeleteWhyModal);
        }
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteConclusionModal(false);
        setOpenDeleteProblemStatementModal(false);
        setOpenDeleteWhyModal(null);
    };

    const disableWhy = useMemo(
        () => isSaving || !newAnswerText?.trim() || !newQuestionText?.trim(),
        [isSaving, newQuestionText, newAnswerText],
    );

    const disableProblemStatement = useMemo(
        () => isSaving || !problemStatementText?.trim(),
        [isSaving, problemStatementText],
    );

    const disableRootCauseConclusion = useMemo(
        () => isSaving || !rootCauseConclusionText?.trim(),
        [isSaving, rootCauseConclusionText],
    );

    const deleteModalTranslation = useMemo(
        () =>
            openDeleteProblemStatementModal
                ? "problem_statement_delete_modal"
                : openDeleteConclusionModal
                ? "root_cause_conclusion_delete_modal"
                : "why_delete_modal",
        [openDeleteProblemStatementModal, openDeleteConclusionModal],
    );

    if (!fiveWhyAnalisys || (fiveWhyAnalisys && fiveWhyAnalisys.length === 0)) {
        return <NoData />;
    }

    return (
        <div>
            <div className={classes.tittle}>
                <span className={classes.label}>
                    {t("rca.rca_library.rca_detail.5_whys_analysis.problem_statement")}
                </span>
                {!isEditingProblemStatement && (
                    <div className={classes.boxButtons}>
                        <IconButton
                            disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                            onClick={handleEditionOnProblemStatement}
                        >
                            <EditIcon />
                        </IconButton>
                        <IconButton
                            disabled={!RCA_PERMISSIONS?.rca_library.canEdit || disableProblemStatement}
                            onClick={() => {
                                setOpenDeleteProblemStatementModal(true);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </div>
                )}
            </div>
            <div className={classes.editableBox}>
                {!isEditingProblemStatement && (
                    <span className={classes.plainText}>{fiveWhyAnalisys[0].problem_statement}</span>
                )}
                {isEditingProblemStatement && (
                    <>
                        <TextField
                            placeholder={t("rca.rca_library.rca_detail.5_whys_analysis.problem_statement_placeholder")}
                            aria-labelledby="outlined-newTaskDescription-label"
                            fullWidth
                            disabled={isSaving}
                            id="outlined-newTaskDescription-input"
                            className={mainClasses.contentFormTextField}
                            name="problemStatement"
                            type="text"
                            variant="outlined"
                            value={problemStatementText}
                            onChange={(event) => {
                                setProblemStatementText(event.target.value);
                            }}
                        />
                        <div className={classes.editableBoxButtons}>
                            <Button
                                className={mainClasses.contentButton}
                                variant="contained"
                                color="error"
                                onClick={() => {
                                    setIsEditingProblemStatement(false);
                                    setProblemStatementText(rca.five_why_analyses[0]?.problem_statement || "");
                                }}
                            >
                                <ClearIcon />
                                {t("rca.rca_library.rca_detail.5_whys_analysis.cancel")}
                            </Button>
                            <Button
                                className={mainClasses.contentButton}
                                variant="contained"
                                color="info"
                                disabled={disableProblemStatement}
                                onClick={() => {
                                    const currentFiveWhyAnalisys = {
                                        ...fiveWhyAnalisys[0],
                                        problem_statement: problemStatementText,
                                    };
                                    handleOnSave(currentFiveWhyAnalisys);
                                }}
                            >
                                <CheckIcon />
                                {t("rca.rca_library.rca_detail.5_whys_analysis.save")}
                            </Button>
                        </div>
                    </>
                )}
            </div>

            <Divider />
            <div className={classes.questionsAndAnswersWrapper}>
                {fiveWhyAnalisys && !fiveWhyAnalisys[0].why && <NoData />}
                {fiveWhyAnalisys &&
                    fiveWhyAnalisys[0].why &&
                    fiveWhyAnalisys[0].why.map((why, index) => {
                        return (
                            <div key={index}>
                                <div className={classes.questionsAndAnswersItemWrapper}>
                                    {why.primary_why_id ? (
                                        <>
                                            <div
                                                className={classes.aWrapper}
                                                style={{
                                                    ...(questionToEdit === why.primary_why_id && {
                                                        marginTop: 16,
                                                    }),
                                                }}
                                            >
                                                <span className={classes.aIcon}>
                                                    {t("rca.rca_library.rca_detail.5_whys_analysis.a")}
                                                </span>
                                                {(questionToEdit === null || questionToEdit !== why.primary_why_id) && (
                                                    <span className={classes.aText}>{why.text}</span>
                                                )}
                                                {questionToEdit === why.primary_why_id && (
                                                    <div className={classes.editableBox}>
                                                        <TextField
                                                            placeholder={t(
                                                                "rca.rca_library.rca_detail.5_whys_analysis.answer_placeholder",
                                                            )}
                                                            aria-labelledby="outlined-newTaskDescription-label"
                                                            fullWidth
                                                            id="outlined-newTaskDescription-input"
                                                            className={mainClasses.contentFormTextField}
                                                            name="answerToEdit"
                                                            type="text"
                                                            onKeyDown={async (event) => {
                                                                if (event.key === "Enter") {
                                                                    event.preventDefault();
                                                                    setQuestionToEdit(null);
                                                                }
                                                            }}
                                                            variant="outlined"
                                                            value={newAnswerText}
                                                            onChange={(event) => {
                                                                setNewAnswerText(event.target.value);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            {questionToEdit === why.primary_why_id && (
                                                <div className={classes.editableBoxButtons}>
                                                    <Button
                                                        className={mainClasses.contentButton}
                                                        variant="contained"
                                                        color="error"
                                                        onClick={() => setQuestionToEdit(null)}
                                                    >
                                                        <ClearIcon />
                                                        {t("rca.rca_library.rca_detail.5_whys_analysis.cancel")}
                                                    </Button>
                                                    <Button
                                                        className={mainClasses.contentButton}
                                                        variant="contained"
                                                        disabled={disableWhy}
                                                        color="info"
                                                        onClick={() => {
                                                            handleSaveEditWhy(why);
                                                        }}
                                                    >
                                                        <CheckIcon />
                                                        {t("rca.rca_library.rca_detail.5_whys_analysis.save")}
                                                    </Button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className={classes.qWrapper}>
                                            <div className={classes.qLeftContent}>
                                                <span className={classes.qIcon}>
                                                    {t("rca.rca_library.rca_detail.5_whys_analysis.q")}
                                                </span>

                                                {(questionToEdit === null || questionToEdit !== why.why_id) && (
                                                    <span className={classes.qText}>{why.text}</span>
                                                )}
                                                {questionToEdit === why.why_id && (
                                                    <div className={classes.editableBox}>
                                                        <TextField
                                                            placeholder={t(
                                                                "rca.rca_library.rca_detail.5_whys_analysis.question_placeholder",
                                                            )}
                                                            aria-labelledby="outlined-newTaskDescription-label"
                                                            fullWidth
                                                            id="outlined-newTaskDescription-input"
                                                            className={mainClasses.contentFormTextField}
                                                            name="questionToEdit"
                                                            type="text"
                                                            prefix={t(
                                                                "rca.rca_library.rca_detail.5_whys_analysis.question_placeholder",
                                                            )}
                                                            onKeyDown={async (event) => {
                                                                if (event.key === "Enter") {
                                                                    event.preventDefault();
                                                                    setQuestionToEdit(null);
                                                                }
                                                            }}
                                                            variant="outlined"
                                                            value={newQuestionText}
                                                            onChange={(event) => {
                                                                setNewQuestionText(event.target.value);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                            {questionToEdit !== why.why_id && (
                                                <div className={classes.boxButtons}>
                                                    <IconButton
                                                        disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                                                        onClick={() => {
                                                            handleEditWhy(why);
                                                        }}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        disabled={!RCA_PERMISSIONS?.rca_library.canEdit || isSaving}
                                                        onClick={() => {
                                                            setOpenDeleteWhyModal(why);
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {fiveWhyAnalisys[0].why &&
                                    index !== fiveWhyAnalisys[0].why.length - 1 &&
                                    index % 2 !== 0 && <Divider />}
                            </div>
                        );
                    })}
            </div>

            {addNewWhy && (
                <>
                    <Divider />
                    <div className={classes.questionsAndAnswersItemWrapper}>
                        <div className={classes.qWrapper}>
                            <div className={classes.qLeftContent}>
                                <span className={classes.qIcon}>
                                    {t("rca.rca_library.rca_detail.5_whys_analysis.q")}
                                </span>
                                <div className={classes.editableBox}>
                                    <TextField
                                        placeholder={t(
                                            "rca.rca_library.rca_detail.5_whys_analysis.question_placeholder",
                                        )}
                                        aria-labelledby="outlined-newTaskDescription-label"
                                        fullWidth
                                        id="outlined-newTaskDescription-input"
                                        className={mainClasses.contentFormTextField}
                                        name="questionToAdd"
                                        type="text"
                                        prefix={t("rca.rca_library.rca_detail.5_whys_analysis.question_prefix")}
                                        onKeyDown={async (event) => {
                                            if (event.key === "Enter") {
                                                event.preventDefault();
                                                setQuestionToEdit(null);
                                            }
                                        }}
                                        variant="outlined"
                                        value={newQuestionText}
                                        onChange={(event) => {
                                            setNewQuestionText(event.target.value);
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div
                            className={classes.aWrapper}
                            style={{
                                marginTop: 16,
                            }}
                        >
                            <span className={classes.aIcon}> {t("rca.rca_library.rca_detail.5_whys_analysis.a")}</span>
                            <div className={classes.editableBox}>
                                <TextField
                                    placeholder={t("rca.rca_library.rca_detail.5_whys_analysis.answer_placeholder")}
                                    aria-labelledby="outlined-newTaskDescription-label"
                                    fullWidth
                                    id="outlined-newTaskDescription-input"
                                    className={mainClasses.contentFormTextField}
                                    name="answerToEdit"
                                    type="text"
                                    onKeyDown={async (event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            setQuestionToEdit(null);
                                        }
                                    }}
                                    variant="outlined"
                                    value={newAnswerText}
                                    onChange={(event) => {
                                        setNewAnswerText(event.target.value);
                                    }}
                                />
                            </div>
                        </div>
                        <div className={classes.editableBoxButtons}>
                            <Button
                                className={mainClasses.contentButton}
                                variant="contained"
                                color="error"
                                onClick={() => setAddNewWhy(false)}
                            >
                                <ClearIcon />
                                {t("rca.rca_library.rca_detail.5_whys_analysis.cancel")}
                            </Button>
                            <Button
                                className={mainClasses.contentButton}
                                variant="contained"
                                disabled={disableWhy}
                                color="info"
                                onClick={handleSaveNewWhy}
                            >
                                <CheckIcon />
                                {t("rca.rca_library.rca_detail.5_whys_analysis.save")}
                            </Button>
                        </div>
                    </div>
                </>
            )}

            <Divider />
            <div className={classes.addAnotherWhyQAWrapper}>
                <MainCardButtonAction
                    disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                    title={t("rca.rca_library.rca_detail.5_whys_analysis.another_why_q_and_a")}
                    renderIcon={() => <AddCircleIcon />}
                    onClick={handleAddNewWhy}
                />
            </div>

            <Divider />
            <div className={classes.rootCauseConclusionWrapper}>
                <div className={classes.tittle}>
                    <span className={classes.label}>
                        {t("rca.rca_library.rca_detail.5_whys_analysis.root_cause_conclusion")}
                    </span>
                    {!isEditingRootCauseConclusion && (
                        <div className={classes.boxButtons}>
                            <IconButton
                                disabled={!RCA_PERMISSIONS?.rca_library.canEdit}
                                onClick={handleEditionOnRootCauseConclusion}
                            >
                                <EditIcon />
                            </IconButton>

                            <IconButton
                                disabled={!RCA_PERMISSIONS?.rca_library.canEdit || disableRootCauseConclusion}
                                onClick={() => {
                                    setOpenDeleteConclusionModal(true);
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </div>
                    )}
                </div>

                <div className={classes.editableBox}>
                    {!isEditingRootCauseConclusion && (
                        <span className={classes.plainText}>{fiveWhyAnalisys[0].root_cause_conclusion}</span>
                    )}
                    {isEditingRootCauseConclusion && (
                        <>
                            <TextField
                                placeholder={t(
                                    "rca.rca_library.rca_detail.5_whys_analysis.root_cause_conclusion_placeholder",
                                )}
                                aria-labelledby="outlined-newTaskDescription-label"
                                fullWidth
                                id="outlined-newTaskDescription-input"
                                className={mainClasses.contentFormTextField}
                                name="rootCauseConclusion"
                                type="text"
                                variant="outlined"
                                value={rootCauseConclusionText}
                                onChange={(event) => {
                                    setRootCauseConclusionText(event.target.value);
                                }}
                            />
                            <div className={classes.editableBoxButtons}>
                                <Button
                                    className={mainClasses.contentButton}
                                    variant="contained"
                                    color="error"
                                    onClick={() => {
                                        setIsEditingRootCauseConclusion(!isEditingRootCauseConclusion);
                                        setRootCauseConclusionText(
                                            rca.five_why_analyses[0]?.root_cause_conclusion || "",
                                        );
                                    }}
                                >
                                    <ClearIcon />
                                    {t("rca.rca_library.rca_detail.5_whys_analysis.cancel")}
                                </Button>
                                <Button
                                    disabled={disableRootCauseConclusion}
                                    className={mainClasses.contentButton}
                                    variant="contained"
                                    color="info"
                                    onClick={() => {
                                        const currentFiveWhyAnalisys = {
                                            ...fiveWhyAnalisys[0],
                                            root_cause_conclusion: rootCauseConclusionText,
                                        };
                                        handleOnSave(currentFiveWhyAnalisys);
                                    }}
                                >
                                    <CheckIcon />
                                    {t("rca.rca_library.rca_detail.5_whys_analysis.save")}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <RCAConfirmDeleteModal
                open={openDeleteProblemStatementModal || openDeleteConclusionModal || !!openDeleteWhyModal}
                onClose={handleCloseDeleteModal}
                onDeleteItem={handleDeletion}
                title={t(`rca.rca_library.rca_detail.5_whys_analysis.${deleteModalTranslation}.title`)}
                warning={t(`rca.rca_library.rca_detail.5_whys_analysis.${deleteModalTranslation}.warning`)}
                confirm={t(`rca.rca_library.rca_detail.5_whys_analysis.${deleteModalTranslation}.confirm`)}
            />
        </div>
    );
};

export default RCA5WhysAnalisys;
